import React from 'react';
import { AxiosResponse } from 'axios';
import { SidebarAd } from 'googlead/components/SidebarAd';
import { useMustacheTranslation, useOTSTranslation } from 'i18n/useOTSTranslation';
import { WEATHER_PAGE_SIZE } from 'ots-constants';
import {
  fetchRegionFeatured, fetchRegionMeta,
  fetchRegionOpeningDate, fetchRegionRelated, fetchRegionRelatedNews,
  fetchRegionResortsByStatus,
} from 'api/region/static';
import { IRegionResortsPaginated, IRegionShort } from 'api/region/types';
import { IRelatedNews, ResortStatus } from 'api/resort/types';
import { CurrentLink, CurrentTab } from 'components/region/RegionHeaderNav/constants';
import { WeatherReports } from 'components/region/WeatherReports';
import { BoxWithShadow } from 'components/blocks/BoxWithShadow';
import { HeaderNavCurLink } from 'components/common/HeaderNav';
import { BlockContent } from 'components/blocks/BlockContent';
import { BlockHeader } from 'components/blocks/BlockHeader';
import { Main } from 'components/resort/Layout/Main';
import { Side } from 'components/resort/Layout/Side';
import { useRegionLayoutBubble } from 'page-components/region/_context';
import { IRegionLayout } from 'page-components/region/_layout';
import { RouteAndAnalyticsProps } from 'page-components/pathTypes';
import { getRegionSlug } from 'util/memcache';

import PlanYourTrip from 'components/common/PlanYourTrip/RegionWidget';
import { Hero400Error } from 'components/400';
import NoResortsBlock from 'components/blocks/NoResortBlock';
import { formatDateFromStr, MINIMAL_DATE_FORMAT } from 'util/formatDate';
import { assertShouldBeNot404, wrapServerSideProps, wrapStaticProps } from 'util/wrapProps';
import SurfaceRelatedArticles from 'components/resort/SurfaceRelatedArticles';

export type RegionOpenResortsPageProps = {
  resorts: IRegionResortsPaginated,
  openingDate: string,
  numResorts: number,
  pathInfo: IRegionShort,
  relatedNews: IRelatedNews | null,
} & RouteAndAnalyticsProps & IRegionLayout;

export const DEBUG_TAG = 'region-open-resorts-page';
export const TEMPLATE_ID = '2.1.3';
export const TEMPLATE_NAME = 'Region Open Resorts';

export default function RegionOpenResortsPage({
  openingDate, resorts, pathInfo, numResorts,
  relatedNews,
}: RegionOpenResortsPageProps) {
  useRegionLayoutBubble({});
  const { t } = useOTSTranslation('common');
  const m = useMustacheTranslation('common');

  if (!resorts) {
    return <Hero400Error light />;
  }

  const seoTitle = pathInfo.isLogical ? t('region.openResorts.logicSeoTitle', { region: pathInfo.title })
    : t('region.openResorts.seoTitle', { region: pathInfo.title });

  const seoText = pathInfo.isLogical ? m('region.openResorts.logicSeoText', {
    region: pathInfo.title,
    numresorts: numResorts,
    firstopendate: formatDateFromStr(openingDate, MINIMAL_DATE_FORMAT),
  }) : m('region.openResorts.seoText', {
    region: pathInfo.title,
    numresorts: numResorts,
    firstopendate: formatDateFromStr(openingDate, MINIMAL_DATE_FORMAT),
  });

  return (
    <>
      <Main>
        <BoxWithShadow>
          <BlockHeader title={seoTitle} />
          <BlockContent>
            {seoText}
          </BlockContent>
        </BoxWithShadow>
        {!!resorts.pagination?.count && (
          <WeatherReports
            regionId={pathInfo.uuid}
            resorts={resorts}
            openFlag={ResortStatus.openResorts}
            isFresh
          />
        )}
        {!resorts.pagination?.count && (
          <NoResortsBlock text={t('region.openResorts.noResorts', { region: pathInfo.title })} />
        )}
        <PlanYourTrip
          region={pathInfo}
          hasSkiRental={false}
          hasCarRental={false}
          hasFlights={false}
          isPartner={false}
        />
      </Main>
      <Side>
        <SurfaceRelatedArticles
          relatedNewsList={relatedNews}
          isFresh
        />
        <SidebarAd isRegion id={pathInfo.uuid} showWeSki={false} />
      </Side>
    </>
  );
}

export const fetchProps = async ({ region }: { region: string }): Promise<RegionOpenResortsPageProps> => {
  const cache: AxiosResponse<IRegionShort> = await assertShouldBeNot404(() => getRegionSlug(region));
  const { uuid } = cache.data;
  const featured = await fetchRegionFeatured(uuid);

  return {
    type: 'region',
    route: 'open-resorts',
    resorts: await fetchRegionResortsByStatus(uuid, ResortStatus.openResorts, 0, WEATHER_PAGE_SIZE),
    openingDate: await fetchRegionOpeningDate(uuid),
    featured,
    pathInfo: cache.data,
    numResorts: (await fetchRegionMeta(uuid))?.resorts?.active,
    relatedNews: await fetchRegionRelatedNews(uuid),

    /**
       * Layout
       */
    related: await fetchRegionRelated(uuid),
    navigation: {
      head: HeaderNavCurLink.SNOW_REPORTS,
      tab: CurrentTab.TRIP_PLANNING,
      link: CurrentLink.OPEN_RESORTS,
    },
    adTKey: 'metaregion open-resorts',
    templateName: TEMPLATE_NAME,
    templateId: TEMPLATE_ID,
    hero: {
      title: 'region.openResorts.heroTitle',
    },
    meta: {
      redirectTo: cache.data.domain ?? null,
      description: 'MetaRegion.open_resorts.description',
      title: 'MetaRegion.open_resorts.title',
    },
    prefetch: false,
  };
};

// This function gets called at build time
// eslint-disable-next-line max-len
export const getRegionOpenResortsPageStaticProps = wrapStaticProps(fetchProps);
export const getRegionOpenResortsPageServerSideProps = wrapServerSideProps(fetchProps);
